import { useEffect, useMemo, useState } from "react";
import { CalendarDays, MapPin, Trophy, Users, Filter } from "lucide-react";
import api from "@/lib/api";

const STATUS = {
  live: "bg-red-500 text-white animate-pulse",
  upcoming: "bg-talanta-gold/20 text-amber-800",
  completed: "bg-talanta-charcoal/10 text-talanta-charcoal/60",
};

const fmt = (d) => new Date(d).toLocaleDateString("en-KE", { day: "numeric", month: "short" });
const range = (a, b) => (a === b ? fmt(a) : `${fmt(a)} – ${fmt(b)}`);

export const TournamentCalendar = ({ onOpenSport }) => {
  const [items, setItems] = useState([]);
  const [county, setCounty] = useState("all");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/sport/tournaments")
      .then(({ data }) => setItems(data.tournaments || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  const counties = useMemo(() => ["all", ...Array.from(new Set(items.map((t) => t.county))).sort()], [items]);
  const shown = useMemo(
    () => items.filter((t) => t.status !== "completed" && (county === "all" || t.county === county)).sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    [items, county]
  );

  return (
    <section id="tournaments" data-testid="tournament-calendar" className="relative bg-white py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-xl">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-terracotta">Tournament calendar</span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-talanta-green">Upcoming NextGen tournaments</h2>
            <p className="mt-3 text-talanta-charcoal/70">Live from the Talanta Sport engine. Filter by county to find events near you.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="county-filter">
            <Filter className="h-4 w-4 text-talanta-charcoal/40" />
            {counties.map((c) => (
              <button
                key={c}
                data-testid={`county-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCounty(c)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                  county === c ? "bg-talanta-green text-white border-talanta-green" : "bg-white text-talanta-green border-talanta-green/20 hover:bg-talanta-cream"
                }`}
              >
                {c === "all" ? "All counties" : c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((t) => (
            <article key={t.id} data-testid="tournament-card" className="group rounded-2xl border border-talanta-green/10 bg-talanta-cream p-5 hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className="flex items-center justify-between">
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${STATUS[t.status]}`}>{t.status === "live" ? "● Live now" : t.status}</span>
                <span className="text-xs font-semibold text-talanta-terracotta">{t.sport}</span>
              </div>
              <h3 className="mt-3 font-display font-bold text-lg text-talanta-green leading-snug">{t.name}</h3>
              <div className="mt-3 space-y-1.5 text-sm text-talanta-charcoal/70">
                <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-talanta-green/60" /> {range(t.starts_at, t.ends_at)}</div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-talanta-green/60" /> {t.venue}</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-talanta-green/60" /> {t.teams} teams · <Trophy className="h-4 w-4 text-talanta-gold" /> KES {Number(t.prize_pool_kes).toLocaleString()}</div>
              </div>
              <button onClick={onOpenSport} className="mt-4 text-sm font-semibold text-talanta-red group-hover:underline">Open in Talanta Sport →</button>
            </article>
          ))}
          {loaded && shown.length === 0 && (
            <p data-testid="tournament-empty" className="text-talanta-charcoal/50 text-sm col-span-full">No upcoming tournaments in this county yet.</p>
          )}
        </div>
      </div>
    </section>
  );
};
